import { create } from "zustand";
import { devtools } from "zustand/middleware";

interface Project {
  id: string;
  order: string;
  title: string;
  description: string;
  create_time: string;
  last_update: string;
  banner: string;
  about_id: string;
  technology: string;
  user_count: string;
  type?: number;
  link?: string;
  video_path?: string;
  /** Client-facing outcome line */
  outcome?: string;
  /** Filter category */
  category?: "Web" | "Mobile" | "Product";
  role?: string;
}

export type { Project };

interface Skill {
  name: string;
  percentage: number;
  category: "Front-end" | "Back-end" | "Deployment";
}

interface PortfolioState {
  // UI State
  sliderValue: number;
  typedText: string;
  showBackToTop: boolean;
  navbarBackground: boolean;

  // Modal State
  showModal: boolean;
  modalLoading: boolean;
  screenshots: Array<{ path: string }>;
  currentSlide: number;

  // Data
  projects: Project[];
  skills: Skill[];
  loading: boolean;
  pageLoaded: boolean;

  // Actions
  setSliderValue: (value: number) => void;
  setTypedText: (text: string) => void;
  setShowBackToTop: (show: boolean) => void;
  setNavbarBackground: (background: boolean) => void;
  setShowModal: (show: boolean) => void;
  setModalLoading: (loading: boolean) => void;
  setScreenshots: (screenshots: Array<{ path: string }>) => void;
  setCurrentSlide: (slide: number) => void;
  setProjects: (projects: Project[]) => void;
  setSkills: (skills: Skill[]) => void;
  setLoading: (loading: boolean) => void;
  setPageLoaded: (loaded: boolean) => void;

  // Complex Actions
  showScreenshots: (projectId: string, type: number) => void;
  closeModal: () => void;
  startTypingAnimation: () => void;
  handleScroll: () => void;
}

const defaultSkills: Skill[] = [
  // Front-end
  { name: "TypeScript", percentage: 90, category: "Front-end" },
  { name: "JavaScript", percentage: 90, category: "Front-end" },
  { name: "JQuery", percentage: 90, category: "Front-end" },
  { name: "React Native", percentage: 90, category: "Front-end" },
  { name: "React", percentage: 90, category: "Front-end" },
  { name: "Angular", percentage: 90, category: "Front-end" },
  { name: "Vue.js", percentage: 90, category: "Front-end" },
  { name: "Next.js", percentage: 90, category: "Front-end" },
  { name: "Tailwind CSS", percentage: 90, category: "Front-end" },
  { name: "Material UI", percentage: 90, category: "Front-end" },
  { name: "Bootstrap", percentage: 90, category: "Front-end" },
  { name: "HTML", percentage: 90, category: "Front-end" },
  { name: "CSS", percentage: 90, category: "Front-end" },
  { name: "LESS", percentage: 90, category: "Front-end" },
  { name: "Sass", percentage: 90, category: "Front-end" },

  // Back-end
  { name: "Go", percentage: 90, category: "Back-end" },

  { name: "Python", percentage: 90, category: "Back-end" },
  { name: "Flask", percentage: 90, category: "Back-end" },
  { name: "FastAPI", percentage: 90, category: "Back-end" },

  { name: "Node.js", percentage: 90, category: "Back-end" },
  { name: "Express", percentage: 90, category: "Back-end" },

  { name: "PHP", percentage: 90, category: "Back-end" },
  { name: "CodeIgniter", percentage: 90, category: "Back-end" },
  { name: "Laravel", percentage: 80, category: "Back-end" },

  { name: "Convex", percentage: 90, category: "Back-end" },

  { name: "MySQL", percentage: 90, category: "Back-end" },
  { name: "PostgreSQL", percentage: 90, category: "Back-end" },
  { name: "MongoDB", percentage: 85, category: "Back-end" },

  // Deployment
  { name: "Git", percentage: 90, category: "Deployment" },
  { name: "Docker", percentage: 90, category: "Deployment" },
  { name: "AWS", percentage: 90, category: "Deployment" },
  { name: "Linux", percentage: 80, category: "Deployment" },
  { name: "Azure", percentage: 70, category: "Deployment" },
  { name: "GCP", percentage: 70, category: "Deployment" },
];

export const usePortfolioStore = create<PortfolioState>()(
  devtools(
    (set) => ({
      // Initial State
      sliderValue: 50,
      typedText: "",
      showBackToTop: false,
      navbarBackground: false,
      showModal: false,
      modalLoading: false,
      screenshots: [],
      currentSlide: 0,
      projects: [],
      skills: defaultSkills,
      loading: true,
      pageLoaded: false,

      // Basic Setters
      setSliderValue: (value) => set({ sliderValue: value }),
      setTypedText: (text) => set({ typedText: text }),
      setShowBackToTop: (show) => set({ showBackToTop: show }),
      setNavbarBackground: (background) =>
        set({ navbarBackground: background }),
      setShowModal: (show) => set({ showModal: show }),
      setModalLoading: (loading) => set({ modalLoading: loading }),
      setScreenshots: (screenshots) => set({ screenshots }),
      setCurrentSlide: (slide) => set({ currentSlide: slide }),
      setProjects: (projects) => set({ projects }),
      setSkills: (skills) => set({ skills }),
      setLoading: (loading) => set({ loading }),
      setPageLoaded: (loaded) => set({ pageLoaded: loaded }),

      // Complex Actions
      showScreenshots: (projectId: string) => {
        set({ modalLoading: true, showModal: true });

        // Simulate loading screenshots based on project ID
        setTimeout(() => {
          let mockScreenshots: Array<{ path: string }> = [];

          // Generate different screenshots based on project ID
          switch (projectId) {
            case "15": // Utalk CRM
              mockScreenshots = [
                { path: "utalkcrm/AdminStsudentStatistics.png" },
                {
                  path: "utalkcrm/Head-Teacher--check-Timeslot-Availability.jpg",
                },
                { path: "utalkcrm/EvaluationSystem_QA.png" },
                { path: "utalkcrm/SCSystem.png" },
                { path: "utalkcrm/Admin_Agent_SchoolManager.png" },
              ];
              break;
            case "13": // Payroll System
              mockScreenshots = [
                { path: "payroll/ir.png" },
                { path: "payroll/home.png" },
                { path: "payroll/login.png" },
              ];
              break;
            case "11": // Student Online/Offline Class App
              mockScreenshots = [
                { path: "joystud/teacher_list.png" },
                { path: "joystud/booking1.png" },
                { path: "joystud/booking2.png" },
                { path: "joystud/booking3.png" },
                { path: "joystud/booking4.png" },
                { path: "joystud/hw.png" },
                { path: "joystud/grade.png" },
              ];
              break;
            case "12": // Student Online Class App
              mockScreenshots = [{ path: "utalkstud/macbook.png" }];
              break;
            case "16": // Backtester Trading Platform
              mockScreenshots = [
                { path: "backtester/Wallets.jpeg" },
                { path: "backtester/Twitter.jpeg" },
              ];
              break;
            case "17": // Event Monitor Dashboard
              mockScreenshots = [
                { path: "eventmonitor/charts.jpg" },
                { path: "eventmonitor/db.jpg" },
                { path: "eventmonitor/dashboard.jpg" },
              ];
              break;
            case "18": // Utalk CMS
              mockScreenshots = [
                { path: "utalkcms/Pages.png" },
                { path: "utalkcms/Login.png" },
              ];
              break;
            case "19": // Vidal Management System
              mockScreenshots = [
                { path: "vidal/user.jpg" },
                { path: "vidal/dash.jpg" },
              ];
              break;
            case "20": // Airdrop Platform
              mockScreenshots = [{ path: "airdrop/macbook.jpg" }];
              break;
            case "21": // HR Management System
              mockScreenshots = [
                { path: "hr/HomePage.png" },
                { path: "hr/login.png" },
                { path: "hr/statistics.png" },
              ];
              break;
            case "22": // Inventory Management System
              mockScreenshots = [
                { path: "inventory/1.png" },
                { path: "inventory/home.png" },
                { path: "inventory/login.png" },
              ];
              break;
            case "23": // ABFish Platform
              mockScreenshots = [{ path: "abfish/home.jpg" }];
              break;
            case "24": // Ambassador Program Platform
              mockScreenshots = [
                { path: "ambassador/Scan.png" },
                { path: "ambassador/Home.png" },
                { path: "ambassador/PC.png" },
              ];
              break;
            case "25": // Wedding Planning Platform
              mockScreenshots = [{ path: "wedding/macbook.png" }];
              break;
            case "26": // SaniServices Platform
              mockScreenshots = [
                { path: "saniservices/Desktop2.png" },
                { path: "saniservices/Mobile.png" },
                { path: "saniservices/Mobile2.png" },
                { path: "saniservices/Desktop.png" },
              ];
              break;
            case "27": // Trader Platform
              mockScreenshots = [
                { path: "trader/Screenshot 2023-07-10 at 9.22.32 PM.png" },
                { path: "trader/home.png" },
              ];
              break;
            case "28": // VSA Platform
              mockScreenshots = [{ path: "vsa/1.jpeg" }];
              break;
            case "29": // Real Estate Platform
              mockScreenshots = [{ path: "realestate/home.png" }];
              break;
            case "30": // Health Training Platform
              mockScreenshots = [{ path: "healthtraining/Page.png" }];
              break;
            case "31": // TimelessTweets Platform
              mockScreenshots = [
                { path: "timelessTweets/home.jpeg" },
                { path: "timelessTweets/tweets.jpeg" },
              ];
              break;
            case "tarif-portal":
              mockScreenshots = [{ path: "tarifportal/banner.jpeg" }];
              break;
            case "gunbay":
              mockScreenshots = [
                { path: "gunbay/gb-home.jpeg" },
                { path: "gunbay/gb-profile.jpeg" },
                { path: "gunbay/gb-searches.jpeg" },
              ];
              break;
            case "camo":
              mockScreenshots = [
                { path: "camo/admin.png" },
                { path: "camo/camo-start.png" },
                { path: "camo/friend-profile.png" },
                { path: "camo/friend.png" },
                { path: "camo/home.png" },
                { path: "camo/home-2.png" },
                { path: "camo/map.png" },
                { path: "camo/my-tool.png" },
                { path: "camo/posts.png" },
                { path: "camo/profile.png" },
                { path: "camo/request.png" },
                { path: "camo/scouting.png" },
                { path: "camo/signin.png" },
              ];
              break;
            case "food-order-app":
              mockScreenshots = [
                { path: "food-order-app/glamorous-touch.jpg" },
                { path: "food-order-app/ground-coffe.jpg" },
                { path: "food-order-app/pump-penang.jpg" },
                { path: "food-order-app/sisi-hai-xian.jpg" },
                { path: "food-order-app/swiety-michail.jpg" },
              ];
              break;
            case "fitzone":
              mockScreenshots = [
                { path: "fitzone/home.jpeg" },
                { path: "fitzone/mobile.jpeg" },
                { path: "fitzone/desktop.jpeg" },
              ];
              break;
            default:
              mockScreenshots = [
                { path: "demo/screenshot1.png" },
                { path: "demo/screenshot2.png" },
                { path: "demo/screenshot3.png" },
              ];
          }

          set({ screenshots: mockScreenshots, modalLoading: false });
        }, 800);
      },

      closeModal: () => {
        set({
          showModal: false,
          screenshots: [],
          currentSlide: 0,
        });
      },

      startTypingAnimation: () => {
        const baseText = "Hey, I am ";
        const firstName = "Don";
        const secondName = "Coder";
        let currentText = baseText;
        let isTyping = true;
        let currentIndex = 0;
        let currentWord = firstName;
        let isFirstWord = true;

        const typeText = () => {
          if (isTyping) {
            // Typing phase
            if (currentIndex < currentWord.length) {
              currentText =
                baseText + currentWord.substring(0, currentIndex + 1);
              set({ typedText: currentText });
              currentIndex++;
              setTimeout(typeText, 100);
            } else {
              // Finished typing current word, pause for 2 seconds
              isTyping = false;
              setTimeout(typeText, 2000);
            }
          } else {
            // Backspace phase
            if (currentIndex > 0) {
              currentText =
                baseText + currentWord.substring(0, currentIndex - 1);
              set({ typedText: currentText });
              currentIndex--;
              setTimeout(typeText, 100);
            } else {
              // Finished backspacing, switch to next word
              isTyping = true;
              currentIndex = 0;

              if (isFirstWord) {
                currentWord = secondName;
                isFirstWord = false;
              } else {
                currentWord = firstName;
                isFirstWord = true;
              }

              setTimeout(typeText, 100);
            }
          }
        };

        // Start the animation
        typeText();
      },

      handleScroll: () => {
        const scrollPos = window.scrollY;

        set({
          showBackToTop: scrollPos > 0,
          navbarBackground: scrollPos >= 100,
        });
      },
    }),
    {
      name: "portfolio-store",
    }
  )
);
